import { create } from 'zustand';
import {
  Connection,
  EdgeChange,
  NodeChange,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
} from 'reactflow';
import type { Node as ReactFlowNode, Edge as ReactFlowEdge } from 'reactflow';
import type { NodeData, CreateNodePayload, UpdateNodePayload } from '@/types/node.types';
import { NODE_TYPE_COLORS } from '@/types/node.types';
import type { LinkData, CreateLinkPayload, UpdateLinkPayload } from '@/types/link.types';
import type { Canvas } from '@/types/canvas.types';
import canvasesApi from '@/api/canvases.api';
import nodesApi from '@/api/nodes.api';
import linksApi from '@/api/links.api';

// Estimated node dimensions for handle routing
const NODE_WIDTH = 200;
const NODE_HEIGHT = 100;

// Given two node positions, pick the best handles (closest sides)
function computeBestHandles(
  sourcePos: { x: number; y: number },
  targetPos: { x: number; y: number },
): { sourceHandle: string; targetHandle: string } {
  const sCx = sourcePos.x + NODE_WIDTH / 2;
  const sCy = sourcePos.y + NODE_HEIGHT / 2;
  const tCx = targetPos.x + NODE_WIDTH / 2;
  const tCy = targetPos.y + NODE_HEIGHT / 2;

  const dx = tCx - sCx;
  const dy = tCy - sCy;

  let sourceSide: string;
  let targetSide: string;

  if (Math.abs(dx) > Math.abs(dy)) {
    // Horizontal dominance
    if (dx > 0) {
      sourceSide = 'right';
      targetSide = 'left';
    } else {
      sourceSide = 'left';
      targetSide = 'right';
    }
  } else {
    // Vertical dominance
    if (dy > 0) {
      sourceSide = 'bottom';
      targetSide = 'top';
    } else {
      sourceSide = 'top';
      targetSide = 'bottom';
    }
  }

  return {
    sourceHandle: `${sourceSide}-source`,
    targetHandle: `${targetSide}-target`,
  };
}

// Recompute all edge handles based on current node positions
function rerouteEdges(
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[],
): ReactFlowEdge[] {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  return edges.map((edge) => {
    const sourceNode = nodeMap.get(edge.source);
    const targetNode = nodeMap.get(edge.target);
    if (!sourceNode || !targetNode) return edge;

    const { sourceHandle, targetHandle } = computeBestHandles(
      sourceNode.position,
      targetNode.position,
    );

    if (edge.sourceHandle === sourceHandle && edge.targetHandle === targetHandle) {
      return edge; // no change
    }

    return { ...edge, sourceHandle, targetHandle };
  });
}

// Backend entity types (what the API returns)
interface ApiNode {
  id: string;
  title: string;
  description?: string;
  type: string;
  priority: number;
  status: string;
  tags: string[];
  positionX: number;
  positionY: number;
  canvasId: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiLink {
  id: string;
  type: string;
  label?: string;
  intensity: string;
  description?: string;
  sourceNodeId: string;
  targetNodeId: string;
  canvasId: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

// Transform backend node → React Flow node
function toReactFlowNode(apiNode: ApiNode): ReactFlowNode<NodeData> {
  return {
    id: apiNode.id,
    type: apiNode.type,
    position: { x: apiNode.positionX, y: apiNode.positionY },
    data: {
      title: apiNode.title,
      description: apiNode.description,
      type: apiNode.type as NodeData['type'],
      status: apiNode.status as NodeData['status'],
      priority: apiNode.priority as NodeData['priority'],
      color: NODE_TYPE_COLORS[apiNode.type as keyof typeof NODE_TYPE_COLORS] || '#6B7280',
      canvasId: apiNode.canvasId,
      createdById: apiNode.createdById,
      tags: apiNode.tags,
      createdAt: apiNode.createdAt,
      updatedAt: apiNode.updatedAt,
    },
  };
}

// Transform backend link → React Flow edge
function toReactFlowEdge(apiLink: ApiLink): ReactFlowEdge<LinkData> {
  return {
    id: apiLink.id,
    type: apiLink.type,
    source: apiLink.sourceNodeId,
    target: apiLink.targetNodeId,
    data: {
      linkType: apiLink.type as LinkData['linkType'],
      intensity: apiLink.intensity as LinkData['intensity'],
      label: apiLink.label,
      description: apiLink.description,
      canvasId: apiLink.canvasId,
      createdBy: apiLink.createdById,
      createdAt: apiLink.createdAt,
      updatedAt: apiLink.updatedAt,
    },
  };
}

interface CanvasState {
  canvas: Canvas | null;
  nodes: ReactFlowNode<NodeData>[];
  edges: ReactFlowEdge<LinkData>[];
  isLoading: boolean;
  error: string | null;

  loadCanvas: (canvasId: string) => Promise<void>;
  clearCanvas: () => void;

  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;

  addNode: (payload: CreateNodePayload) => Promise<ReactFlowNode<NodeData>>;
  updateNode: (nodeId: string, payload: UpdateNodePayload) => Promise<void>;
  removeNode: (nodeId: string) => Promise<void>;

  addEdge: (payload: CreateLinkPayload) => Promise<ReactFlowEdge<LinkData>>;
  updateEdge: (edgeId: string, payload: UpdateLinkPayload) => Promise<void>;
  removeEdge: (edgeId: string) => Promise<void>;
}

export const useCanvasStore = create<CanvasState>((set, get) => ({
  canvas: null,
  nodes: [],
  edges: [],
  isLoading: false,
  error: null,

  loadCanvas: async (canvasId: string) => {
    set({ isLoading: true, error: null });
    try {
      const [canvas, apiNodes, apiLinks] = await Promise.all([
        canvasesApi.getById(canvasId),
        nodesApi.listByCanvas(canvasId),
        linksApi.listByCanvas(canvasId),
      ]);
      const rfNodes = (apiNodes || []).map(toReactFlowNode);
      const rfEdges = (apiLinks || []).map(toReactFlowEdge);
      set({
        canvas,
        nodes: rfNodes,
        edges: rerouteEdges(rfNodes, rfEdges),
        isLoading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load canvas';
      set({ error: message, isLoading: false });
    }
  },

  clearCanvas: () => {
    set({ canvas: null, nodes: [], edges: [], error: null });
  },

  onNodesChange: (changes: NodeChange[]) => {
    const newNodes = applyNodeChanges(changes, get().nodes);
    const hasPositionChange = changes.some((c) => c.type === 'position');
    if (hasPositionChange) {
      set({ nodes: newNodes, edges: rerouteEdges(newNodes, get().edges) });
    } else {
      set({ nodes: newNodes });
    }
  },

  onEdgesChange: (changes: EdgeChange[]) => {
    set({ edges: applyEdgeChanges(changes, get().edges) });
  },

  onConnect: (connection: Connection) => {
    set({ edges: addEdge(connection, get().edges) });
  },

  addNode: async (payload: CreateNodePayload) => {
    const apiNode = await nodesApi.create(payload);
    const node = toReactFlowNode(apiNode);
    set({ nodes: [...get().nodes, node] });
    return node;
  },

  updateNode: async (nodeId: string, payload: UpdateNodePayload) => {
    const apiNode = await nodesApi.update(nodeId, payload);
    const updated = toReactFlowNode(apiNode);
    set({
      nodes: get().nodes.map((n) => (n.id === nodeId ? updated : n)),
    });
  },

  removeNode: async (nodeId: string) => {
    await nodesApi.delete(nodeId);
    set({
      nodes: get().nodes.filter((n) => n.id !== nodeId),
      edges: get().edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
    });
  },

  addEdge: async (payload: CreateLinkPayload) => {
    const apiLink = await linksApi.create(payload);
    const edge = toReactFlowEdge(apiLink);
    const newEdges = [...get().edges, edge];
    set({ edges: rerouteEdges(get().nodes, newEdges) });
    return edge;
  },

  updateEdge: async (edgeId: string, payload: UpdateLinkPayload) => {
    const apiLink = await linksApi.update(edgeId, payload);
    const updated = toReactFlowEdge(apiLink);
    set({
      edges: get().edges.map((e) => (e.id === edgeId ? updated : e)),
    });
  },

  removeEdge: async (edgeId: string) => {
    await linksApi.delete(edgeId);
    set({
      edges: get().edges.filter((e) => e.id !== edgeId),
    });
  },
}));

export default useCanvasStore;
